import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ReactPlayer from 'react-player';
import VideoCard from '../components/VideoCard';
import { youtubeService } from '../services/youtubeService';
import { addToWatchHistory, isLiked, addToLikedVideos, removeFromLikedVideos } from '../utils/localStorage';
import './VideoPlayer.css';

const VideoPlayer = () => {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [channel, setChannel] = useState(null);
  const [comments, setComments] = useState([]);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribed, setSubscribed] = useState(false);
  const [liked, setLiked] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  const loadVideoData = useCallback(async () => {
    setLoading(true);
    
    const videoData = await youtubeService.getVideoDetails(videoId);
    setVideo(videoData);
    
    // Add to watch history
    if (videoData) {
      addToWatchHistory(videoData);
      setLiked(isLiked(videoId));
    }

    if (videoData) {
      const channelData = await youtubeService.getChannelDetails(
        videoData.snippet.channelId
      );
      setChannel(channelData);

      const commentsData = await youtubeService.getVideoComments(videoId);
      setComments(commentsData);

      // Get related videos based on video title/tags
      if (videoData?.snippet) {
        try {
          // Extract keywords from title for better search
          const keywords = videoData.snippet.title
            .split(' ')
            .slice(0, 3) // Take first 3 words
            .join(' ');
          
          const relatedData = await youtubeService.searchVideos(keywords, 15);
          
          // Filter out the current video and get details
          const filteredVideos = relatedData.filter(v => {
            const vId = v.id.videoId || v.id;
            return vId !== videoId;
          });
          
          // Fetch full details for related videos
          if (filteredVideos.length > 0) {
            const relatedDetails = await Promise.all(
              filteredVideos.slice(0, 10).map(async (v) => {
                const vId = v.id.videoId || v.id;
                const details = await youtubeService.getVideoDetails(vId);
                return details;
              })
            );
            setRelatedVideos(relatedDetails.filter(Boolean));
          }
        } catch (error) {
          console.error('Error fetching related videos:', error);
          setRelatedVideos([]);
        }
      }
    }

    setLoading(false);
  }, [videoId]);

  useEffect(() => {
    loadVideoData();
    window.scrollTo(0, 0);
  }, [videoId, loadVideoData]);
  
  const handleLikeClick = () => {
    if (video) {
      if (liked) {
        removeFromLikedVideos(videoId);
        setLiked(false);
      } else {
        addToLikedVideos(video);
        setLiked(true);
      }
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading || !video) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading video...</p>
      </div>
    );
  }

  return (
    <div className="video-player-page">
      <div className="player-section">
        <div className="player-wrapper">
          <ReactPlayer
            url={`https://www.youtube.com/watch?v=${videoId}`}
            controls
            width="100%"
            height="100%"
            playing
          />
        </div>

        <div className="video-info-section">
          <h1 className="video-title">{video.snippet.title}</h1>

          <div className="video-meta">
            <div className="video-stats">
              <span>{formatNumber(video.statistics.viewCount)} views</span>
              <span>•</span>
              <span>{formatDate(video.snippet.publishedAt)}</span>
            </div>

            <div className="video-actions">
              <button 
                className={`action-btn ${liked ? 'liked' : ''}`}
                onClick={handleLikeClick}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                </svg>
                {formatNumber(video.statistics.likeCount)}
              </button>
              <button className="action-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/>
                </svg>
              </button>
              <button className="action-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                </svg>
                Share
              </button>
              <button className="action-btn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M17 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H5V5h10v4z"/>
                </svg>
                Save
              </button>
            </div>
          </div>
        </div>

        {channel && (
          <div className="channel-info">
            <div className="channel-details">
              <div className="channel-avatar">
                {channel.snippet.thumbnails?.default?.url ? (
                  <img src={channel.snippet.thumbnails.default.url} alt={channel.snippet.title} />
                ) : (
                  channel.snippet.title.charAt(0).toUpperCase()
                )}
              </div>
              <div className="channel-text">
                <h3>{channel.snippet.title}</h3>
                <p>{formatNumber(channel.statistics.subscriberCount)} subscribers</p>
              </div>
            </div>
            <button 
              className={`subscribe-btn ${subscribed ? 'subscribed' : ''}`}
              onClick={() => setSubscribed(!subscribed)}
            >
              {subscribed ? 'Subscribed' : 'Subscribe'}
            </button>
          </div>
        )}

        <div className={`video-description ${showFullDescription ? 'expanded' : ''}`}>
          <p>{video.snippet.description}</p>
          {video.snippet.description.length > 200 && (
            <button 
              className="show-more-btn"
              onClick={() => setShowFullDescription(!showFullDescription)}
            >
              {showFullDescription ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>

        <div className="comments-section">
          <h2 className="comments-title">
            {formatNumber(video.statistics.commentCount)} Comments
          </h2>

          <div className="comments-list">
            {comments.map((comment, index) => {
              const snippet = comment.snippet.topLevelComment.snippet;
              return (
                <div 
                  key={comment.id} 
                  className="comment"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="comment-avatar">
                    {snippet.authorProfileImageUrl ? (
                      <img src={snippet.authorProfileImageUrl} alt={snippet.authorDisplayName} />
                    ) : (
                      snippet.authorDisplayName.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="comment-content">
                    <div className="comment-header">
                      <span className="comment-author">{snippet.authorDisplayName}</span>
                      <span className="comment-date">{formatDate(snippet.publishedAt)}</span>
                    </div>
                    <p className="comment-text">{snippet.textDisplay}</p>
                    <div className="comment-actions">
                      <button className="comment-action">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z"/>
                        </svg>
                        {snippet.likeCount > 0 && snippet.likeCount}
                      </button>
                      <button className="comment-action">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                          <path d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05c-.09.23-.14.47-.14.73v2c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"/>
                        </svg>
                      </button>
                      <button className="comment-action">Reply</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="related-videos">
        <h3 className="related-title">Related Videos</h3>
        {loading ? (
          <div className="related-loading">
            <div className="spinner"></div>
            <p>Loading recommendations...</p>
          </div>
        ) : relatedVideos.length > 0 ? (
          relatedVideos.map((relatedVideo, index) => (
            <div 
              key={relatedVideo.id || relatedVideo.id?.videoId || index}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <VideoCard video={relatedVideo} />
            </div>
          ))
        ) : (
          <p className="no-related">No related videos found</p>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
